import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute, { AdminProtectedRoute, SuperAdminProtectedRoute } from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import RestaurantHome from "./pages/RestaurantHome";
import Book from "./pages/Book";
import MyReservations from "./pages/MyReservations";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminTables from "./pages/AdminTables";
import AdminSlots from "./pages/AdminSlots";
import AdminReservations from "./pages/AdminReservations";
import AdminSettings from "./pages/AdminSettings";
import AdminMenu from "./pages/AdminMenu";
import AdminDiscounts from "./pages/AdminDiscounts";
import AdminGallery from "./pages/AdminGallery";
import AdminIntegrations from "./pages/AdminIntegrations";
import AdminBilling from "./pages/AdminBilling";
import AdminBillingReturn from "./pages/AdminBillingReturn";
import AdminBillingCardSaved from "./pages/AdminBillingCardSaved";
import AdminReviews from "./pages/AdminReviews";
import PaymentReturn from "./pages/PaymentReturn";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminRestaurants from "./pages/SuperAdminRestaurants";
import SuperAdminNewRestaurant from "./pages/SuperAdminNewRestaurant";
import SuperAdminRestaurantDetail from "./pages/SuperAdminRestaurantDetail";
import SuperAdminBilling from "./pages/SuperAdminBilling";
import PrivateMirror from "./pages/PrivateMirror";
import RestaurantSignup from "./pages/RestaurantSignup";
import ActivateRestaurateur from "./pages/ActivateRestaurateur";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import CookiePolicy from "./pages/CookiePolicy";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";

function AppRouter() {
    const location = useLocation();

    // Reset scroll to top on every route change
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [location.pathname]);

    // Handle OAuth callback when session_id in hash (any route)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    // Hide global Renova chrome (Navbar/Footer/CookieBanner) on Private Mirror
    // pages and on bookings opened via a mirror link, so the customer never
    // sees branding pointing them to other restaurants or to login.
    const isPrivateMirror = location.pathname.startsWith("/m/");
    const fromMirror = new URLSearchParams(location.search).get("from") === "mirror";
    const isMirrorContext = isPrivateMirror || fromMirror;

    return (
        <>
            {!isMirrorContext && <Navbar />}
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/iscrizione-ristoratore" element={<RestaurantSignup />} />
                <Route path="/attiva-ristoratore" element={<ActivateRestaurateur />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/my-reservations" element={<ProtectedRoute><MyReservations /></ProtectedRoute>} />

                {/* Restaurant-scoped customer routes */}
                <Route path="/r/:slug" element={<RestaurantHome />} />
                <Route path="/r/:slug/book" element={<ProtectedRoute><Book /></ProtectedRoute>} />

                {/* Private Mirror — standalone restaurant page (no Renova branding for the customer) */}
                <Route path="/m/:token" element={<PrivateMirror />} />

                {/* Restaurateur admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
                <Route path="/admin/tables" element={<AdminProtectedRoute><AdminTables /></AdminProtectedRoute>} />
                <Route path="/admin/slots" element={<AdminProtectedRoute><AdminSlots /></AdminProtectedRoute>} />
                <Route path="/admin/menu" element={<AdminProtectedRoute><AdminMenu /></AdminProtectedRoute>} />
                <Route path="/admin/discounts" element={<AdminProtectedRoute><AdminDiscounts /></AdminProtectedRoute>} />
                <Route path="/admin/gallery" element={<AdminProtectedRoute><AdminGallery /></AdminProtectedRoute>} />
                <Route path="/admin/reservations" element={<AdminProtectedRoute><AdminReservations /></AdminProtectedRoute>} />
                <Route path="/admin/reviews" element={<AdminProtectedRoute><AdminReviews /></AdminProtectedRoute>} />
                <Route path="/admin/billing" element={<AdminProtectedRoute><AdminBilling /></AdminProtectedRoute>} />
                <Route path="/admin/billing/return" element={<AdminProtectedRoute><AdminBillingReturn /></AdminProtectedRoute>} />
                <Route path="/admin/billing/card-saved" element={<AdminProtectedRoute><AdminBillingCardSaved /></AdminProtectedRoute>} />
                <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettings /></AdminProtectedRoute>} />
                <Route path="/admin/integrations" element={<AdminProtectedRoute><AdminIntegrations /></AdminProtectedRoute>} />

                {/* Payment return */}
                <Route path="/payment/return" element={<PaymentReturn />} />

                {/* Super admin (Renova) */}
                <Route path="/adminrenova/login" element={<SuperAdminLogin />} />
                <Route path="/adminrenova" element={<SuperAdminProtectedRoute><SuperAdminDashboard /></SuperAdminProtectedRoute>} />
                <Route path="/adminrenova/restaurants" element={<SuperAdminProtectedRoute><SuperAdminRestaurants /></SuperAdminProtectedRoute>} />
                <Route path="/adminrenova/restaurants/new" element={<SuperAdminProtectedRoute><SuperAdminNewRestaurant /></SuperAdminProtectedRoute>} />
                <Route path="/adminrenova/restaurants/:id" element={<SuperAdminProtectedRoute><SuperAdminRestaurantDetail /></SuperAdminProtectedRoute>} />
                <Route path="/adminrenova/billing" element={<SuperAdminProtectedRoute><SuperAdminBilling /></SuperAdminProtectedRoute>} />
            </Routes>
            {!isMirrorContext && <Footer />}
            {!isMirrorContext && <CookieBanner />}
        </>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                    <Toaster theme="dark" position="top-right" richColors />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
